import { publicAgent, getPortfolio } from './bluesky';
import { atproto } from './atproto';

export interface ExportData {
  profile: any;
  portfolio: any;
  notes: any[];
  exportedAt: string;
}

export type ExportTheme = 'gold' | 'white' | 'blue' | 'grey';

export async function aggregateExportData(actor: string): Promise<ExportData> {
  const [profileRes, portfolio, notes] = await Promise.all([
    publicAgent.getProfile({ actor }),
    getPortfolio(actor),
    atproto.getPosts(actor)
  ]);

  return {
    profile: profileRes.data,
    portfolio,
    notes: notes || [],
    exportedAt: new Date().toISOString()
  };
}

export function generatePortfolioHtml(data: ExportData, theme: ExportTheme = 'gold'): string {
  // Deep clone and sanitize data for export (strip _tempUrl)
  const sanitizedData = JSON.parse(JSON.stringify(data, (key, value) => {
    if (key === '_tempUrl') return undefined;
    return value;
  }));
  
  const jsonStr = JSON.stringify(sanitizedData).replace(/</g, '\\u003c');
  
  const themeStyles = {
    gold: {
      primary: '#DBA858',
      bg: '#0f0f0f',
      surface: '#1a1a1a',
      text: '#ffffff',
      textDim: '#999999',
      glass: 'rgba(26, 26, 26, 0.8)',
      border: 'rgba(255, 255, 255, 0.05)'
    },
    white: {
      primary: '#2563eb',
      bg: '#f8fafc',
      surface: '#ffffff',
      text: '#0f172a',
      textDim: '#64748b',
      glass: 'rgba(255, 255, 255, 0.9)',
      border: 'rgba(0, 0, 0, 0.05)'
    },
    blue: {
      primary: '#38bdf8',
      bg: '#0c4a6e',
      surface: '#075985',
      text: '#f1f5f9',
      textDim: '#7dd3fc',
      glass: 'rgba(7, 89, 133, 0.8)',
      border: 'rgba(255, 255, 255, 0.1)'
    },
    grey: {
      primary: '#a3a3a3',
      bg: '#171717',
      surface: '#262626',
      text: '#f5f5f5',
      textDim: '#737373',
      glass: 'rgba(38, 38, 38, 0.8)',
      border: 'rgba(255, 255, 255, 0.05)'
    }
  };

  const activeTheme = themeStyles[theme] || themeStyles.gold;

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${data.profile.displayName || data.profile.handle} - Portfolio</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&family=Outfit:wght@400;700;900&display=swap" rel="stylesheet">
    <style>
        :root {
            --primary: ${activeTheme.primary};
            --bg: ${activeTheme.bg};
            --surface: ${activeTheme.surface};
            --card-bg: ${activeTheme.glass};
            --text: ${activeTheme.text};
            --text-dim: ${activeTheme.textDim};
            --border: ${activeTheme.border};
            --danger: #ff4444;
            --success: #4CAF50;
            --accent-yellow: #FACC15;
            --gold-glow: ${theme === 'gold' ? '0 0 20px rgba(219, 168, 88, 0.2)' : 'none'};
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Inter', sans-serif; 
            background: var(--bg); 
            color: var(--text); 
            line-height: 1.6;
            overflow-x: hidden;
        }
        h1, h2, h3, .heading-font { font-family: 'Outfit', sans-serif; }

        .container { max-width: 1000px; margin: 0 auto; padding: 2rem 1rem; }
        
        .glass {
            background: var(--card-bg);
            backdrop-filter: blur(16px);
            border: 1px solid var(--border);
            border-radius: 1.5rem;
            box-shadow: var(--gold-glow);
        }

        .header {
            position: relative;
            padding: 5rem 2rem;
            text-align: center;
            margin-bottom: 2rem;
            overflow: hidden;
            border-radius: 2rem;
        }
        .header-banner {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background-size: cover;
            background-position: center;
            opacity: 0.4;
            filter: grayscale(0.5);
            z-index: 0;
        }
        .header-content { position: relative; z-index: 1; }
        .avatar {
            width: 140px; height: 140px;
            border-radius: 50%;
            border: 4px solid var(--primary);
            margin: 0 auto 1.5rem;
            display: block;
            object-fit: cover;
        }

        .tabs {
            display: flex;
            gap: 0.5rem;
            margin-bottom: 2.5rem;
            background: rgba(0,0,0,0.1);
            padding: 0.5rem;
            border-radius: 1.25rem;
            border: 1px solid var(--border);
        }
        .tab-btn {
            flex: 1;
            padding: 1rem;
            border: none;
            background: transparent;
            color: var(--text-dim);
            cursor: pointer;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 1px;
            font-size: 0.75rem;
            border-radius: 0.75rem;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .tab-btn.active {
            background: var(--primary);
            color: ${theme === 'white' ? '#fff' : '#000'};
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .tab-content { display: none; }
        .tab-content.active { display: block; animation: slideUp 0.5s ease-out; }

        @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .section-title {
            font-size: 1.5rem;
            font-weight: 900;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: var(--primary);
        }

        .project-card {
            padding: 1.5rem;
            margin-bottom: 1.5rem;
            display: flex;
            gap: 1.5rem;
            transition: all 0.3s;
            position: relative;
        }
        .project-thumb {
            width: 220px; height: 140px;
            border-radius: 1rem;
            object-fit: cover;
            flex-shrink: 0;
            border: 1px solid var(--border);
        }

        .experience-item {
            padding: 1.5rem;
            margin-bottom: 1rem;
            border-left: 3px solid var(--primary);
            position: relative;
        }
        .experience-meta { color: var(--text-dim); font-size: 0.875rem; font-weight: 700; }

        .story-card {
            padding: 2rem 0;
            margin-bottom: 0;
            cursor: pointer;
            transition: all 0.3s;
            position: relative;
            display: flex;
            gap: 2rem;
            align-items: flex-start;
            border-bottom: 1px solid var(--border);
        }
        .story-card:last-child { border-bottom: none; }
        .story-info { flex: 1; min-width: 0; }
        .story-title { font-size: 1.5rem; font-weight: 900; margin-bottom: 0.5rem; line-height: 1.2; }
        .story-excerpt { 
            color: var(--text-dim); 
            font-size: 0.95rem; 
            margin-bottom: 1rem;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.5;
        }
        .story-thumbnail-container {
            width: 120px; height: 120px;
            flex-shrink: 0;
            border-radius: 0.75rem;
            overflow: hidden;
            border: 1px solid var(--border);
            background: rgba(0,0,0,0.2);
        }
        .story-thumbnail { width: 100%; height: 100%; object-fit: cover; }
        .story-meta { 
            color: var(--text-dim); 
            font-size: 0.75rem; 
            font-weight: 700; 
            text-transform: uppercase; 
            letter-spacing: 0.5px;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .contact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1.5rem;
            margin-bottom: 2rem;
        }
        .contact-card {
            padding: 1.5rem;
            display: flex;
            align-items: center;
            gap: 1rem;
            text-decoration: none;
            color: var(--text);
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .contact-card:hover {
            transform: translateY(-4px);
            border-color: var(--primary);
            background: rgba(var(--primary), 0.05);
        }
        .contact-icon {
            width: 44px; height: 44px;
            background: var(--surface);
            border-radius: 12px;
            display: flex; align-items: center; justify-content: center;
            font-size: 1.25rem;
            border: 1px solid var(--border);
            color: var(--primary);
        }
        .contact-label { font-size: 0.7rem; font-weight: 900; color: var(--text-dim); text-transform: uppercase; letter-spacing: 1px; }
        .contact-value { font-weight: 700; font-size: 1rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

        .header-menu-container {
            position: absolute;
            top: 1.5rem; right: 1.5rem;
            z-index: 100;
        }
        .menu-dots {
            background: transparent;
            border: none;
            color: var(--text);
            width: 40px; height: 40px;
            border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            cursor: pointer;
            font-size: 1.5rem;
            transition: all 0.2s;
            backdrop-filter: none;
        }
        .menu-dots:hover { color: var(--primary); transform: scale(1.1); }

        .menu-dropdown {
            position: absolute;
            top: 100%; right: 0;
            margin-top: 0.5rem;
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: 1rem;
            width: 200px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.5);
            display: none;
            overflow: hidden;
            z-index: 1000;
        }
        .menu-dropdown.active { display: block; animation: menuScale 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
        @keyframes menuScale {
            from { opacity: 0; transform: scale(0.95) translateY(-10px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .menu-item {
            padding: 1rem 1.5rem;
            color: var(--text);
            font-size: 0.85rem;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.2s;
            display: flex; align-items: center; gap: 0.75rem;
        }
        .menu-item:hover { background: rgba(255,255,255,0.05); color: var(--primary); }
        .menu-item.danger { color: var(--danger); }
        .menu-item.danger:hover { background: rgba(255, 68, 68, 0.1); }

        .story-viewer {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            background: var(--bg);
            z-index: 1000;
            display: none;
            overflow-y: auto;
        }
        .story-viewer.active { display: block; }
        .viewer-content { max-width: 800px; margin: 5rem auto; padding: 0 1.5rem; }
        
        .login-btn { display: none; }
        .logout-btn { display: none; }

        .social-tag {
            background: var(--border);
            padding: 0.5rem 1rem;
            border-radius: 0.75rem;
            font-size: 0.75rem;
            font-weight: 900;
            color: var(--text);
            border: 1px solid var(--border);
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: all 0.2s;
        }
        .social-tag:hover { border-color: var(--primary); transform: translateY(-2px); }
        .social-label { color: var(--primary); font-size: 0.6rem; letter-spacing: 1px; }

        .btn-small {
            padding: 0.4rem 0.8rem;
            font-size: 0.7rem;
            font-weight: 900;
            border-radius: 0.5rem;
            cursor: pointer;
            border: none;
            text-transform: uppercase;
            transition: all 0.2s;
        }
        .btn-primary { background: var(--primary); color: ${theme === 'white' ? '#fff' : '#000'}; }
        .btn-danger { background: var(--danger); color: #fff; }
        .btn-success { background: var(--success); color: #fff; }
        .btn-accent { background: var(--accent-yellow); color: #000; }
        .btn-white { background: #fff; color: #000; }
        .btn-white:hover { background: #f0f0f0; }

        input, textarea, select {
            background: rgba(0,0,0,0.05);
            border: 1px solid var(--border);
            padding: 1rem;
            border-radius: 0.75rem;
            color: var(--text);
            width: 100%;
            margin-bottom: 1rem;
            font-family: inherit;
        }
        input:focus, textarea:focus, select:focus { border-color: var(--primary); outline: none; }
        
        select option {
            background: #fff;
            color: #000;
        }

        .modal-overlay {
            position: fixed; inset: 0;
            background: rgba(0,0,0,0.9);
            backdrop-filter: blur(10px);
            z-index: 2000;
            display: none;
            align-items: center; justify-content: center;
            padding: 1rem;
        }

        .editor-container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 2rem;
            width: 100%;
            max-width: 1350px;
            height: 90vh;
        }

        .editor-scroll {
            background: var(--surface);
            padding: 2rem;
            border-radius: 1.5rem;
            overflow-y: auto;
            position: relative;
        }

        .preview-scroll {
            background: var(--bg);
            padding: 4rem;
            border-radius: 1.5rem;
            overflow-y: auto;
            border: 1px solid var(--border);
        }

        @media (max-width: 900px) {
            .editor-container { grid-template-columns: 1fr; }
            .preview-scroll { display: none; }
        }

        .story-block {
            position: relative;
            margin-bottom: 2rem;
            padding: 2rem;
            border: 1px solid var(--border);
            border-radius: 1.25rem;
            background: rgba(0,0,0,0.1);
            transition: all 0.3s ease-in-out;
        }
        .story-block:hover { border-color: var(--primary); }
        .block-tools {
            position: absolute;
            top: -24px; right: 15px;
            display: flex; gap: 6px;
            z-index: 10;
            opacity: 0.3;
            transition: opacity 0.2s;
        }
        .story-block:hover .block-tools { opacity: 1; }
        
        .drag-handle {
            cursor: move;
            padding: 0.3rem 0.6rem;
            background: var(--surface);
            border: 1px solid var(--border);
            border-radius: 0.5rem;
            font-size: 0.7rem;
            font-weight: 900;
            color: var(--primary);
        }

        .insert-bar {
            height: 20px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.2;
            transition: all 0.3s;
            position: relative;
            z-index: 50;
        }
        .insert-bar:hover { opacity: 1; height: 60px; }
        
        .insert-indicator {
            width: 32px;
            height: 32px;
            background: var(--primary);
            color: #000;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.2rem;
            font-weight: 900;
            cursor: pointer;
            transition: all 0.3s;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .insert-bar:hover .insert-indicator { transform: scale(0); opacity: 0; }

        .insert-actions { 
            position: absolute; 
            display: none; 
            gap: 0.4rem; 
            background: var(--surface); 
            padding: 0.6rem; 
            border-radius: 1rem;
            border: 1px solid var(--primary);
            box-shadow: 0 10px 30px rgba(0,0,0,0.5);
            transform: scale(0.8);
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .insert-bar:hover .insert-actions { display: flex; flex-wrap: wrap; max-width: 300px; justify-content: center; transform: scale(1); }

        .block-textarea {
            width: 100%;
            background: transparent;
            border: none;
            color: var(--text);
            resize: none;
            overflow: hidden;
            font-family: inherit;
            font-size: 1rem;
            line-height: 1.7;
            margin-bottom: 0;
            padding: 0;
        }
        .block-textarea:focus { outline: none; }

        .image-preview-mini {
            width: 120px; height: 70px;
            border-radius: 0.5rem;
            object-fit: cover;
            border: 1px solid var(--border);
            margin-bottom: 0.5rem;
            background: #000;
            display: block;
        }

        .draft-badge {
            background: var(--danger);
            color: white;
            font-size: 0.6rem;
            font-weight: 900;
            padding: 0.2rem 0.5rem;
            border-radius: 0.3rem;
            margin-left: 0.5rem;
            vertical-align: middle;
        }
        
        blockquote {
            border-left: 4px solid var(--primary);
            padding-left: 1.5rem;
            margin: 1.5rem 0;
            font-style: italic;
            color: var(--text-dim);
            font-size: 1.25rem;
        }
        
        pre {
            background: #000;
            color: #fff;
            padding: 1.5rem;
            border-radius: 1rem;
            overflow-x: auto;
            border: 1px solid var(--border);
            margin: 1.5rem 0;
            font-family: 'Courier New', Courier, monospace;
            font-size: 0.9rem;
        }
        
        .dash-line {
            height: 1px;
            background: var(--border);
            margin: 2.5rem 0;
            border: none;
            position: relative;
        }
        .dash-line::after {
            content: "◆";
            position: absolute;
            left: 50%; top: 50%;
            transform: translate(-50%, -50%);
            background: var(--bg);
            padding: 0 1rem;
            color: var(--border);
            font-size: 0.8rem;
        }
        .moving { z-index: 100; box-shadow: 0 20px 50px rgba(0,0,0,0.3); }
    </style>
</head>
<body>
    <div id="app" class="container">
        <div id="main-ui">
            <div class="glass header">
                <div id="header-banner" class="header-banner"></div>
                <div class="header-menu-container">
                    <button class="menu-dots" onclick="toggleMenu(event)">⋮</button>
                    <div id="header-menu" class="menu-dropdown">
                        <div class="menu-item" onclick="toggleEditMode()">
                            <span id="menu-edit-label">EDIT PORTFOLIO</span>
                        </div>
                        <div id="menu-logout" class="menu-item danger" onclick="logout()" style="display: none; border-top: 1px solid var(--border);">
                            LOGOUT
                        </div>
                    </div>
                </div>
                <div class="header-content">
                    <img id="user-avatar" class="avatar" src="" alt="">
                    <h1 id="user-name" class="heading-font" style="font-size: 2.5rem; font-weight: 900;"></h1>
                    <p id="user-handle" style="color: var(--primary); font-weight: 900; letter-spacing: 1px;"></p>
                    <p id="user-description" style="margin-top: 1.5rem; color: var(--text-dim); max-width: 600px; margin-left: auto; margin-right: auto;"></p>
                </div>
            </div>

            <div class="tabs">
                <button class="tab-btn active" onclick="switchTab('about')">Portfolio</button>
                <button class="tab-btn" onclick="switchTab('experience')">Experience</button>
                <button class="tab-btn" onclick="switchTab('stories')">Stories</button>
                <button class="tab-btn" onclick="switchTab('contact')">Contact</button>
            </div>

            <div id="tab-about" class="tab-content active">
                <div id="portfolio-bio"></div>
                <div id="additional-sections"></div>
                <div id="ludography-section"></div>
            </div>

            <div id="tab-experience" class="tab-content">
                <div id="experience-list"></div>
            </div>

            <div id="tab-stories" class="tab-content">
                <div id="story-actions" style="margin-bottom: 2rem; display: none;">
                    <button class="glass" onclick="openStoryEditor()" style="width: 100%; padding: 1.5rem; cursor: pointer; color: var(--primary); font-weight: 900; border: 2px dashed var(--primary);">+ CREATE NEW STORY</button>
                </div>
                <div id="stories-list"></div>
            </div>

            <div id="tab-contact" class="tab-content">
                <div class="section-title heading-font">Connect & Contact</div>
                <div id="contact-tab-list"></div>
            </div>
        </div>

        <button class="login-btn" onclick="toggleEditMode()">EDIT PORTFOLIO</button>
        <button class="logout-btn" onclick="logout()">LOGOUT</button>
    </div>

    <!-- Modals -->
    <div id="story-viewer" class="story-viewer">
        <div style="position: fixed; top: 1.5rem; right: 1.5rem; display: flex; gap: 1rem; z-index: 1001;">
            <button id="viewer-edit-btn" class="btn-primary" style="padding: 0.75rem 1.5rem; border-radius: 2rem; cursor: pointer; font-weight: 900; display: none;">EDIT STORY</button>
            <button class="btn-primary" onclick="closeStory()" style="padding: 0.75rem 1.5rem; border-radius: 2rem; cursor: pointer; font-weight: 900; background: var(--surface); color: var(--text);">CLOSE</button>
        </div>
        <div id="viewer-body" class="viewer-content"></div>
    </div>

    <div id="login-modal" class="modal-overlay">
        <div class="glass" style="padding: 2.5rem; width: 100%; max-width: 450px;">
            <h2 class="heading-font" style="margin-bottom: 2rem; color: var(--primary);">Login to Edit</h2>
            <input type="text" id="login-handle" placeholder="Bluesky Handle">
            <input type="password" id="login-pass" placeholder="App Password">
            <button onclick="handleLogin()" class="btn-primary" style="width: 100%; padding: 1rem; border-radius: 0.75rem; font-weight: 900;">Connect Account</button>
            <button onclick="document.getElementById('login-modal').style.display='none'" style="width:100%; margin-top:1rem; background:transparent; border:none; color:var(--text-dim); cursor:pointer;">Cancel</button>
        </div>
    </div>

    <div id="story-editor" class="modal-overlay">
        <div class="editor-container">
            <div class="editor-scroll">
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 2rem;">
                    <h2 id="editor-title-heading" class="heading-font" style="color: var(--primary);">Story Editor</h2>
                    <button onclick="document.getElementById('story-editor').style.display='none'" class="btn-small btn-danger">X CLOSE</button>
                </div>
                <textarea id="editor-title" placeholder="Story Title" class="block-textarea heading-font" style="font-size: 2.5rem; font-weight: 900; border-bottom: 1px solid var(--border); margin-bottom: 2rem;" oninput="autoResize(this); updatePreview()"></textarea>
                
                <div id="editor-blocks-container">
                    <div id="insert-bar-top" class="insert-bar">
                        <div class="insert-actions">
                            <button onclick="addBlockAt(0, 'text')" class="btn-small btn-primary">Text</button>
                            <button onclick="addBlockAt(0, 'h1')" class="btn-small btn-primary">H1</button>
                            <button onclick="addBlockAt(0, 'image')" class="btn-small btn-primary">Img</button>
                            <button onclick="addBlockAt(0, 'quote')" class="btn-small btn-primary">Quote</button>
                            <button onclick="addBlockAt(0, 'code')" class="btn-small btn-primary">Code</button>
                        </div>
                    </div>
                    <div id="editor-blocks"></div>
                    <div id="insert-bar-bottom" style="margin-top:2rem; text-align:center;">
                        <button onclick="addBlock('text')" class="btn-small glass" style="border:1px dashed var(--primary); color:var(--primary); padding: 1rem 2rem;">+ ADD BLOCK AT END</button>
                    </div>
                </div>

                <div style="margin-top: 3rem; background: rgba(0,0,0,0.05); padding: 2rem; border-radius: 1.5rem; border: 1px solid var(--border);">
                    <div style="display: flex; align-items: center; gap: 0.75rem; margin-bottom:1.5rem;">
                        <input type="checkbox" id="editor-draft" style="width: auto; margin-bottom: 0;" onchange="updatePreview()">
                        <label for="editor-draft" style="font-weight: 900; font-size: 0.85rem; color: var(--text-dim); cursor:pointer;">SAVE AS DRAFT (ONLY VISIBLE TO YOU)</label>
                    </div>
                    <button id="save-story-btn" onclick="saveStory()" class="btn-success" style="width: 100%; padding: 1.5rem; border-radius: 1rem; font-weight: 900; font-size: 1.1rem; margin-bottom: 1rem;">SAVE TO BLUESKY</button>
                    <button id="delete-story-btn" onclick="deleteStory()" class="btn-danger" style="width: 100%; padding: 1rem; border-radius: 1rem; font-weight: 900; font-size: 0.9rem; display: none;">DELETE STORY</button>
                </div>
            </div>
            <div class="preview-scroll">
                <div id="editor-preview-content"></div>
            </div>
        </div>
    </div>

    <script id="data-payload" type="application/json">${jsonStr}</script>

    <script>
        let DATA = JSON.parse(document.getElementById('data-payload').textContent);
        let isEditMode = false;
        let currentEditingStory = null;

        function toggleMenu(e) {
            e.stopPropagation();
            const m = document.getElementById('header-menu');
            m.classList.toggle('active');
        }

        window.onclick = function() {
            document.getElementById('header-menu').classList.remove('active');
        };

        function formatDate(iso) {
            const d = new Date(iso);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }

        function autoResize(el) {
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = el.scrollHeight + 'px';
        }

        function ensureAbsolute(url) {
            if (!url) return '';
            if (url.startsWith('http')) return url;
            if (url.includes('@')) return 'mailto:' + url;
            return 'https://' + url;
        }

        function parseMarkdown(text) {
            if (!text) return '';
            return text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/\\*\\*(.*?)\\*\\*/g, '<b>$1</b>')
                .replace(/\\*(.*?)\\*/g, '<i>$1</i>')
                .replace(/__(.*?)__/g, '<u>$1</u>')
                .replace(/~~(.*?)~~/g, '<s>$1</s>')
                .replace(/\\[(.*?)\\]\\((.*?)\\)/g, (m, label, url) => \`<a href="\${ensureAbsolute(url)}" target="_blank" rel="noopener" style="color:var(--primary); text-decoration:none; font-weight:bold;">\${label}</a>\`)
                .replace(/\\\\n/g, '<br>');
        }

        function getIconFor(type, platform) {
            if (type === 'email') return '✉';
            if (type === 'web') return '🌐';
            const p = platform.toLowerCase();
            if (p.includes('twitter') || p.includes('x')) return '𝕏';
            if (p.includes('github')) return '󰊤';
            if (p.includes('linkedin')) return '󰌻';
            if (p.includes('youtube')) return '󰗃';
            if (p.includes('discord')) return '󰙯';
            return '🔗';
        }

        function resolveImageUrl(did, blob, fallback, item) {
            if (!blob) {
                // Only use tempUrl if no blob exists (during upload)
                if (item && item._tempUrl) return item._tempUrl;
                return fallback || '';
            }
            let cid = typeof blob.ref === 'string' ? blob.ref : (blob.ref?.$link || blob.cid);
            if (!cid) return fallback || '';
            let ext = 'jpeg';
            if (blob.mimeType === 'image/png') ext = 'png';
            else if (blob.mimeType === 'image/gif') ext = 'gif';
            else if (blob.mimeType === 'image/webp') ext = 'webp';
            return 'https://cdn.bsky.app/img/feed_fullsize/plain/' + did + '/' + cid + '@' + ext;
        }

        async function init() {
            const session = JSON.parse(localStorage.getItem('session'));
            if (session) {
                try {
                    const [pRes, sRes] = await Promise.all([
                        fetch('https://bsky.social/xrpc/com.atproto.repo.getRecord?repo=' + session.did + '&collection=id.gamedev.profile&rkey=self'),
                        fetch('https://bsky.social/xrpc/com.atproto.repo.listRecords?repo=' + session.did + '&collection=id.gamedev.notes')
                    ]);
                    if (pRes.ok) {
                        const port = await pRes.json();
                        DATA.portfolio = port.value;
                    }
                    if (sRes.ok) {
                        const stor = await sRes.json();
                        DATA.notes = stor.records;
                    }
                    toggleEditMode(true);
                } catch (e) {
                    console.error('Initial fetch failed', e);
                }
            }
            render();
        }

        function render() {
            const { profile, portfolio, notes } = DATA;
            
            document.getElementById('user-name').textContent = profile.displayName || profile.handle;
            document.getElementById('user-handle').textContent = '@' + profile.handle;
            document.getElementById('user-description').textContent = profile.description || '';
            document.getElementById('user-avatar').src = profile.avatar || '';
            if (profile.banner) document.getElementById('header-banner').style.backgroundImage = 'url(' + profile.banner + ')';

            const contactTabDiv = document.getElementById('contact-tab-list');
            contactTabDiv.innerHTML = '';
            const links = [];
            if (portfolio.contact?.email) links.push({label: 'Email', value: portfolio.contact.email, type: 'email'});
            if (portfolio.contact?.website) links.push({label: 'Website', value: portfolio.contact.website, type: 'web'});
            (portfolio.socials || []).forEach(s => links.push({label: s.platform, value: s.url, type: 'social'}));
            
            if (links.length > 0) {
                let chtml = '<div class="contact-grid">';
                links.forEach(l => {
                    const absUrl = l.type === 'email' ? 'mailto:' + l.value : ensureAbsolute(l.value);
                    const icon = getIconFor(l.type, l.label);
                    chtml += '<a href="' + absUrl + '" target="_blank" rel="noopener" class="glass contact-card">';
                    chtml += '<div class="contact-icon">' + icon + '</div>';
                    chtml += '<div class="flex-grow-1 min-width-0">';
                    chtml += '<div class="contact-label">' + l.label + '</div>';
                    chtml += '<div class="contact-value">' + l.value + '</div>';
                    chtml += '</div></a>';
                });
                chtml += '</div>';
                contactTabDiv.innerHTML = chtml;
            } else {
                contactTabDiv.innerHTML = '<div class="glass" style="padding: 3rem; text-align: center; color: var(--text-dim);">No contact information shared.</div>';
            }

            const bioDiv = document.getElementById('portfolio-bio');
            if (isEditMode) {
                bioDiv.innerHTML = \`
                    <div class="glass" style="padding: 2.5rem; margin-bottom: 2.5rem;">
                        <h3 class="heading-font" style="margin-bottom: 1rem; color: var(--primary);">Bio</h3>
                        <textarea id="edit-bio" style="height: 150px;">\${portfolio.bio || ''}</textarea>
                        <button class="btn-small btn-success" onclick="savePortfolioField('bio')">Save Bio</button>
                    </div>
                \`;
            } else if (portfolio.bio) {
                bioDiv.innerHTML = '<div class="glass" style="padding: 2.5rem; margin-bottom: 2.5rem; font-size: 1.1rem; line-height: 1.8;">' + parseMarkdown(portfolio.bio) + '</div>';
            } else { bioDiv.innerHTML = ''; }

            const addDiv = document.getElementById('additional-sections');
            let shtml = isEditMode ? '<div class="section-title heading-font" style="margin-top: 2rem; border-top: 1px dashed var(--border); pt-4">SPECIAL SECTIONS</div>' : '';
            
            const renderSectionInsertBar = (pos) => \`
                <div class="insert-bar" style="opacity: \${isEditMode ? 1 : 0};">
                    <div class="insert-indicator">+</div>
                    <div class="insert-actions">
                        <button onclick="addSectionAt(\${pos}, 'text')" class="btn-small btn-primary">Text</button>
                        <button onclick="addSectionAt(\${pos}, 'h1')" class="btn-small btn-primary">H1</button>
                        <button onclick="addSectionAt(\${pos}, 'image')" class="btn-small btn-primary">Img</button>
                    </div>
                </div>
            \`;

            if (isEditMode) shtml += renderSectionInsertBar(0);

            (portfolio.sections || []).forEach((s, idx) => {
                const imgUrl = resolveImageUrl(profile.did, s.blob, s.content, s);
                if (isEditMode) {
                    shtml += \`
                        <div id="section-\${idx}" class="glass" style="padding: 1.5rem; margin-bottom:0.5rem; position: relative; transition: transform 0.4s ease-in-out;">
                            <div class="block-tools" style="opacity: 1; top: -15px;">
                                <button onclick="moveSection(\${idx}, -1)" class="btn-small btn-white">↑</button>
                                <button onclick="moveSection(\${idx}, 1)" class="btn-small btn-white">↓</button>
                                <button class="btn-small btn-danger" onclick="deleteItem('sections', \${idx})">X</button>
                            </div>
                            <select onchange="updateSectionType(\${idx}, this.value)" style="margin-bottom:0.8rem">
                                <option value="text" \${s.type==='text'?'selected':''}>Text Block</option>
                                <option value="h1" \${s.type==='h1'?'selected':''}>Headline (H1)</option>
                                <option value="image" \${s.type==='image'?'selected':''}>Image</option>
                            </select>
                            \${s.type === 'image' ? \`
                                <div style="display:flex; gap:1rem; align-items:center;">
                                    \${imgUrl ? \`<img src="\${imgUrl}" class="image-preview-mini">\` : ''}
                                    <div style="flex:1;">
                                        <input type="file" onchange="uploadSectionImage(\${idx}, this.files[0])" style="margin-bottom:0.5rem;">
                                        <input type="text" value="\${s.content || ''}" oninput="updateSectionContent(\${idx}, this.value)" placeholder="Image URL">
                                    </div>
                                </div>
                            \` : \`
                                <textarea oninput="autoResize(this); updateSectionContent(\${idx}, this.value)">\${s.content || ''}</textarea>
                            \`}
                            <button class="btn-small btn-success" onclick="pushPortfolioUpdate()" style="margin-top:0.5rem; width:100%">Save Section</button>
                        </div>
                    \`;
                    shtml += renderSectionInsertBar(idx + 1);
                } else {
                    if (s.type === 'h1') shtml += \`<h2 class="heading-font" style="margin-top: 3rem; margin-bottom: 1.5rem; font-size: 2.5rem; color:var(--primary); font-weight:900;">\${s.content}</h2>\`;
                    else if (s.type === 'text') shtml += \`<div class="glass" style="padding: 2rem; margin-bottom: 1.5rem;">\${parseMarkdown(s.content)}</div>\`;
                    else if (s.type === 'image') shtml += \`<img src="\${imgUrl}" style="width: 100%; border-radius: 1.5rem; margin-bottom: 1.5rem; border: 1px solid var(--border);">\`;
                    
                    if (idx < (portfolio.sections.length - 1)) {
                        shtml += '<hr class="dash-line" style="margin: 3rem 0; opacity: 0.3;">';
                    }
                }
            });
            addDiv.innerHTML = shtml;

            const ludDiv = document.getElementById('ludography-section');
            let lhtml = \`<div class="section-title heading-font">Works \${isEditMode ? \`<button class="btn-small btn-primary" onclick="addItem('ludography')">+ ADD WORK</button>\` : ''}</div>\`;
            (portfolio.ludography || []).forEach((game, idx) => {
                if (isEditMode) {
                    lhtml += \`
                        <div id="ludography-\${idx}" class="glass project-card" style="flex-direction: column; position: relative; transition: transform 0.4s ease-in-out;">
                            <div class="block-tools" style="opacity: 1; top: -15px;">
                                <button onclick="moveItem('ludography', \${idx}, -1)" class="btn-small btn-white">↑</button>
                                <button onclick="moveItem('ludography', \${idx}, 1)" class="btn-small btn-white">↓</button>
                                <button class="btn-small btn-danger" onclick="deleteItem('ludography', \${idx})">X</button>
                            </div>
                            <input type="text" id="game-title-\${idx}" value="\${game.title || ''}" placeholder="Title">
                            <input type="text" id="game-role-\${idx}" value="\${game.role || ''}" placeholder="Role">
                            <button class="btn-small btn-success" onclick="saveGame(\${idx})" style="width:100%">Save Work</button>
                        </div>
                    \`;
                } else {
                    const thumb = resolveImageUrl(profile.did, game.blob, game.thumbnail, game);
                    lhtml += \`
                        <div class="glass project-card">
                            \${thumb ? \`<img src="\${thumb}" class="project-thumb">\` : ''}
                            <div>
                                <h3 class="heading-font" style="font-size: 1.5rem;">\${game.title || ''}</h3>
                                <p style="color: var(--primary); font-weight: 900; font-size: 0.85rem;">\${(game.role || '').toUpperCase()} \${game.role && game.year ? ' • ' : ''} \${game.year || ''}</p>
                                \${game.link ? \`<a href="\${ensureAbsolute(game.link)}" target="_blank" rel="noopener" style="display:inline-block; margin-top:1rem; color:var(--primary); text-decoration:none; font-weight:700;">View Project →</a>\` : ''}
                            </div>
                        </div>
                    \`;
                }
            });
            ludDiv.innerHTML = lhtml;

            const expDiv = document.getElementById('experience-list');
            let ehtml = \`<div class="section-title heading-font">Career \${isEditMode ? \`<button class="btn-small btn-primary" onclick="addItem('experiences')">+ ADD JOB</button>\` : ''}</div>\`;
            (portfolio.experiences || []).forEach((exp, idx) => {
                if (isEditMode) {
                    ehtml += \`
                        <div id="experience-\${idx}" class="glass experience-item" style="position: relative; transition: transform 0.4s ease-in-out;">
                            <div class="block-tools" style="opacity: 1; top: -15px;">
                                <button onclick="moveItem('experiences', \${idx}, -1)" class="btn-small btn-white">↑</button>
                                <button onclick="moveItem('experiences', \${idx}, 1)" class="btn-small btn-white">↓</button>
                                <button class="btn-small btn-danger" onclick="deleteItem('experiences', \${idx})">X</button>
                            </div>
                            <div style="display: flex; gap: 1rem;">
                                <div style="flex: 2;">
                                    <input type="text" id="exp-company-\${idx}" value="\${exp.company || ''}" placeholder="Company">
                                    <input type="text" id="exp-position-\${idx}" value="\${exp.position || ''}" placeholder="Position">
                                </div>
                                <div style="flex: 1;">
                                    <input type="text" id="exp-year-\${idx}" value="\${exp.year || ''}" placeholder="Year/Period (e.g. 2021 - 2023)">
                                </div>
                            </div>
                            <textarea oninput="autoResize(this)" id="exp-desc-\${idx}">\${exp.description || ''}</textarea>
                            <button class="btn-small btn-success" onclick="saveExp(\${idx})" style="width:100%">Save Career</button>
                        </div>
                    \`;
                } else if (exp.company || exp.position || exp.description) {
                    ehtml += \`
                        <div class="glass experience-item">
                            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.5rem;">
                                <div>
                                    <h3 class="heading-font" style="font-size: 1.3rem;">\${exp.position || ''}</h3>
                                    <div style="font-weight: 900; color: var(--primary);">\${exp.company || ''}</div>
                                </div>
                                \${exp.year ? \`<div class="experience-meta" style="background: var(--surface); padding: 0.3rem 0.6rem; border-radius: 0.5rem; border:1px solid var(--border);">\${exp.year}</div>\` : ''}
                            </div>
                            <p style="margin-top: 1rem; color: var(--text-dim);">\${parseMarkdown(exp.description || '')}</p>
                        </div>
                    \`;
                }
            });
            expDiv.innerHTML = ehtml;

            const sListDiv = document.getElementById('stories-list');
            sListDiv.innerHTML = '';
            const visibleNotes = notes.filter(n => isEditMode || !n.value.isDraft);
            visibleNotes.forEach((note) => {
                const globalIdx = notes.indexOf(note);
                const blocks = note.value.blocks || [];
                const firstText = blocks.find(b => b.type === 'text' && b.content)?.content || '';
                const cleanExcerpt = firstText.substring(0, 120).replace(/\\n/g, ' ') + (firstText.length > 120 ? '...' : '');
                
                const firstImgBlock = blocks.find(b => b.type === 'image' && (b.blob || b.content));
                const thumbUrl = firstImgBlock ? resolveImageUrl(profile.did, firstImgBlock.blob, firstImgBlock.content, firstImgBlock) : '';

                sListDiv.innerHTML += \`
                    <div class="story-card" onclick="viewStory(\${globalIdx})">
                        <div class="story-info">
                            <div class="story-meta">
                                \${formatDate(note.value.createdAt)}
                                \${note.value.isDraft ? '<span class="draft-badge">DRAFT</span>' : ''}
                            </div>
                            <h3 class="story-title heading-font">\${note.value.title}</h3>
                            <div class="story-excerpt">\${parseMarkdown(cleanExcerpt)}</div>
                            \${isEditMode ? \`<button class="btn-small btn-primary" onclick="event.stopPropagation(); openStoryEditor(\${globalIdx})">EDIT STORY</button>\` : ''}
                        </div>
                        \${thumbUrl ? \`
                            <div class="story-thumbnail-container">
                                <img src="\${thumbUrl}" class="story-thumbnail">
                            </div>
                        \` : ''}
                    </div>
                \`;
            });
            document.getElementById('story-actions').style.display = isEditMode ? 'block' : 'none';
        }

        async function savePortfolioField(field) { DATA.portfolio[field] = document.getElementById('edit-' + field).value; await pushPortfolioUpdate(); }
        async function saveGame(idx) { 
            DATA.portfolio.ludography[idx].title = document.getElementById('game-title-' + idx).value;
            DATA.portfolio.ludography[idx].role = document.getElementById('game-role-' + idx).value;
            await pushPortfolioUpdate(); 
        }
        async function saveExp(idx) {
            DATA.portfolio.experiences[idx].company = document.getElementById('exp-company-' + idx).value;
            DATA.portfolio.experiences[idx].position = document.getElementById('exp-position-' + idx).value;
            DATA.portfolio.experiences[idx].year = document.getElementById('exp-year-' + idx).value;
            DATA.portfolio.experiences[idx].description = document.getElementById('exp-desc-' + idx).value;
            await pushPortfolioUpdate();
        }
        
        function deleteItem(type, idx) {
            if (!confirm('Are you sure?')) return;
            DATA.portfolio[type].splice(idx, 1);
            pushPortfolioUpdate();
        }

        function updateSectionType(idx, type) { DATA.portfolio.sections[idx].type = type; render(); }
        function updateSectionContent(idx, val) { DATA.portfolio.sections[idx].content = val; render(); }
        async function uploadSectionImage(idx, file) {
            if (!file) return;
            DATA.portfolio.sections[idx]._tempUrl = URL.createObjectURL(file);
            render();
            const blob = await uploadBlob(file);
            if (blob) { 
                DATA.portfolio.sections[idx].blob = blob; 
                DATA.portfolio.sections[idx].content = ''; 
                delete DATA.portfolio.sections[idx]._tempUrl; // Clear temp URL after success
                render(); 
            }
        }

        function addSectionAt(idx, type) {
            if (!DATA.portfolio.sections) DATA.portfolio.sections = [];
            DATA.portfolio.sections.splice(idx, 0, { type, content: '' });
            render();
        }

        function moveSection(i, dir) {
            const target = i + dir;
            if (target < 0 || target >= DATA.portfolio.sections.length) return;
            const el1 = document.getElementById('section-' + i);
            const el2 = document.getElementById('section-' + target);
            if (el1 && el2) {
                const diff = el2.offsetTop - el1.offsetTop;
                el1.style.transform = \`translateY(\${diff}px)\`;
                el2.style.transform = \`translateY(\${-diff}px)\`;
                el1.style.zIndex = '100';
            }
            setTimeout(() => {
                [DATA.portfolio.sections[i], DATA.portfolio.sections[target]] = [DATA.portfolio.sections[target], DATA.portfolio.sections[i]];
                render();
            }, 400);
        }

        function moveItem(type, i, dir) {
            const list = DATA.portfolio[type];
            const target = i + dir;
            if (target < 0 || target >= list.length) return;
            const el1 = document.getElementById(type === 'experiences' ? 'experience-' + i : 'ludography-' + i);
            const el2 = document.getElementById(type === 'experiences' ? 'experience-' + target : 'ludography-' + target);
            if (el1 && el2) {
                const diff = el2.offsetTop - el1.offsetTop;
                el1.style.transform = \`translateY(\${diff}px)\`;
                el2.style.transform = \`translateY(\${-diff}px)\`;
                el1.style.zIndex = '100';
            }
            setTimeout(() => {
                [list[i], list[target]] = [list[target], list[i]];
                render();
            }, 400);
        }

        function addItem(type) {
            if (!DATA.portfolio[type]) DATA.portfolio[type] = [];
            let newItem = type === 'sections' ? {type: 'text', content: ''} : (type==='ludography' ? {title:'', role:''} : {company:'', description:''});
            DATA.portfolio[type].unshift(newItem);
            render();
        }

        async function pushPortfolioUpdate() {
            const session = JSON.parse(localStorage.getItem('session'));
            await fetch('https://bsky.social/xrpc/com.atproto.repo.putRecord', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.accessJwt },
                body: JSON.stringify({ repo: session.did, collection: 'id.gamedev.profile', rkey: 'self', record: { $type: 'id.gamedev.profile', ...DATA.portfolio, updatedAt: new Date().toISOString() } })
            });
            alert('Updated!'); render();
        }

        function openStoryEditor(idx = null) {
            currentEditingStory = idx !== null ? JSON.parse(JSON.stringify(DATA.notes[idx])) : { value: { title: '', blocks: [], createdAt: new Date().toISOString(), isDraft: false } };
            document.getElementById('editor-title').value = currentEditingStory.value.title;
            autoResize(document.getElementById('editor-title'));
            document.getElementById('editor-draft').checked = currentEditingStory.value.isDraft || false;
            document.getElementById('story-editor').style.display = 'flex';
            document.getElementById('delete-story-btn').style.display = currentEditingStory.uri ? 'block' : 'none';
            renderEditorBlocks();
            updatePreview();
        }

        function renderEditorBlocks() {
            const container = document.getElementById('editor-blocks');
            container.innerHTML = '';
            
            const firstBar = document.createElement('div');
            firstBar.className = 'insert-bar';
            firstBar.innerHTML = \`
                <div class="insert-indicator">+</div>
                <div class="insert-actions">
                    <button onclick="addBlockAt(0, 'text')" class="btn-small btn-primary">Text</button>
                    <button onclick="addBlockAt(0, 'h1')" class="btn-small btn-primary">H1</button>
                    <button onclick="addBlockAt(0, 'image')" class="btn-small btn-primary">Img</button>
                </div>
            \`;
            container.appendChild(firstBar);

            currentEditingStory.value.blocks.forEach((b, i) => {
                const blockEl = document.createElement('div');
                blockEl.className = 'story-block';
                blockEl.id = 'block-' + i;
                const imgUrl = resolveImageUrl(DATA.profile.did, b.blob, b.content, b);
                blockEl.innerHTML = \`
                    <div class="block-tools">
                        <select onchange="changeBlockType(\${i}, this.value)" class="drag-handle">
                            <option value="text" \${b.type==='text'?'selected':''}>Text</option>
                            <option value="h1" \${b.type==='h1'?'selected':''}>H1</option>
                            <option value="h2" \${b.type==='h2'?'selected':''}>H2</option>
                            <option value="h3" \${b.type==='h3'?'selected':''}>H3</option>
                            <option value="quote" \${b.type==='quote'?'selected':''}>Quote</option>
                            <option value="code" \${b.type==='code'?'selected':''}>Code</option>
                            <option value="image" \${b.type==='image'?'selected':''}>Image</option>
                            <option value="dash" \${b.type==='dash'?'selected':''}>Dash</option>
                        </select>
                        <button onclick="moveBlock(\${i}, -1)" class="btn-small btn-white">↑</button>
                        <button onclick="moveBlock(\${i}, 1)" class="btn-small btn-white">↓</button>
                        <button onclick="removeBlock(\${i})" class="btn-small btn-danger">X</button>
                    </div>
                    <div>
                                       \${b.type === 'image' ? \`
                            <div style="display:flex; gap:1.5rem;">
                                \${imgUrl ? \`<img src="\${imgUrl}" class="image-preview-mini">\` : ''}
                                <div style="flex:1;">
                                    <div style="display:flex; gap:0.5rem; margin-bottom:1rem">
                                        <button onclick="toggleImageSource(\${i}, 'file')" class="btn-small \${(!b.content || b.blob) ? 'btn-success' : 'btn-accent'}" style="flex:1">FILE</button>
                                        <button onclick="toggleImageSource(\${i}, 'url')" class="btn-small \${(b.content && !b.blob) ? 'btn-success' : 'btn-accent'}" style="flex:1">URL</button>
                                    </div>
                                    <div id="image-file-input-\${i}" style="display: \${(!b.content || b.blob) ? 'block' : 'none'}">
                                        <input type="file" onchange="uploadStoryBlockImage(\${i}, this.files[0])">
                                    </div>
                                    <div id="image-url-input-\${i}" style="display: \${(b.content && !b.blob) ? 'block' : 'none'}">
                                        <input type="text" value="\${b.content || ''}" oninput="updateBlock(\${i}, this.value)" placeholder="Image URL">
                                    </div>
                                </div>
                            </div>
                        \` : b.type === 'dash' ? '<div style="text-align:center; padding:1rem; opacity:0.3">SEPARATOR</div>' : \`
                            <textarea class="block-textarea" oninput="autoResize(this); updateBlock(\${i}, this.value)">\${b.content || ''}</textarea>
                        \`}
                    </div>
                \`;
                container.appendChild(blockEl);
                const bar = document.createElement('div');
                bar.className = 'insert-bar';
                bar.innerHTML = \`
                    <div class="insert-indicator">+</div>
                    <div class="insert-actions">
                        <button onclick="addBlockAt(\${i+1}, 'text')" class="btn-small btn-primary">Text</button>
                        <button onclick="addBlockAt(\${i+1}, 'h1')" class="btn-small btn-primary">H1</button>
                        <button onclick="addBlockAt(\${i+1}, 'image')" class="btn-small btn-primary">Img</button>
                    </div>
                \`;
                container.appendChild(bar);
            });
            document.querySelectorAll('.block-textarea').forEach(autoResize);
        }

        function toggleImageSource(idx, type) {
            const block = currentEditingStory.value.blocks[idx];
            if (type === 'file') {
                if (block.content && !block.blob) { block.content = ''; }
            } else {
                if (block.blob) { delete block.blob; delete block._tempUrl; }
            }
            renderEditorBlocks();
            updatePreview();
        }

        function changeBlockType(idx, type) { currentEditingStory.value.blocks[idx].type = type; renderEditorBlocks(); updatePreview(); }
        function addBlock(type) { currentEditingStory.value.blocks.push({type, content: ''}); renderEditorBlocks(); updatePreview(); }
        function addBlockAt(idx, type) { currentEditingStory.value.blocks.splice(idx, 0, {type, content: ''}); renderEditorBlocks(); updatePreview(); }
        function removeBlock(idx) { currentEditingStory.value.blocks.splice(idx, 1); renderEditorBlocks(); updatePreview(); }
        function updateBlock(idx, val) { currentEditingStory.value.blocks[idx].content = val; updatePreview(); }

        function updatePreview() {
            const title = document.getElementById('editor-title').value;
            const isDraft = document.getElementById('editor-draft').checked;
            const preview = document.getElementById('editor-preview-content');
            let html = \`<h1 class="heading-font" style="font-size: 3rem; margin-bottom: 2.5rem; font-weight:900;">\${parseMarkdown(title)} \${isDraft ? '<span class="draft-badge">DRAFT</span>' : ''}</h1>\`;
            currentEditingStory.value.blocks.forEach(b => {
                const url = resolveImageUrl(DATA.profile.did, b.blob, b.content, b);
                if (b.type === 'text') html += \`<p style="margin-bottom:1.5rem; line-height:1.8; opacity:0.8;">\${parseMarkdown(b.content)}</p>\`;
                else if (b.type === 'h1') html += \`<h2 class="heading-font" style="color:var(--primary); margin:2rem 0 1rem; font-size:2rem;">\${parseMarkdown(b.content)}</h2>\`;
                else if (b.type === 'image') html += \`<img src="\${url}" style="width:100%; border-radius:1.5rem; margin:1.5rem 0;">\`;
                else if (b.type === 'quote') html += \`<blockquote>\${parseMarkdown(b.content)}</blockquote>\`;
                else if (b.type === 'code') html += \`<pre>\${b.content}</pre>\`;
                else if (b.type === 'dash') html += '<hr class="dash-line">';
            });
            preview.innerHTML = html;
        }

        function moveBlock(i, dir) {
            const target = i + dir;
            const story = currentEditingStory.value;
            if (target < 0 || target >= story.blocks.length) return;
            
            const el1 = document.getElementById('block-' + i);
            const el2 = document.getElementById('block-' + target);
            
            if (el1 && el2) {
                // Use offsetTop for stable position measurement between siblings
                const diff = el2.offsetTop - el1.offsetTop;
                el1.style.transform = \`translateY(\${diff}px)\`;
                el2.style.transform = \`translateY(\${-diff}px)\`;
                el1.style.zIndex = '100'; // Bring moved item to front
            }
            
            setTimeout(() => {
                [story.blocks[i], story.blocks[target]] = [story.blocks[target], story.blocks[i]];
                renderEditorBlocks(); 
                updatePreview();
            }, 400);
        }

        async function uploadBlob(file) {
            const session = JSON.parse(localStorage.getItem('session'));
            const res = await fetch('https://bsky.social/xrpc/com.atproto.repo.uploadBlob', { method: 'POST', headers: { 'Content-Type': file.type, 'Authorization': 'Bearer ' + session.accessJwt }, body: file });
            return (await res.json()).blob;
        }

        async function uploadStoryBlockImage(idx, file) {
            if (!file) return;
            currentEditingStory.value.blocks[idx]._tempUrl = URL.createObjectURL(file);
            renderEditorBlocks(); updatePreview();
            const blob = await uploadBlob(file);
            if (blob) { 
                currentEditingStory.value.blocks[idx].blob = blob; 
                currentEditingStory.value.blocks[idx].content = ''; 
                delete currentEditingStory.value.blocks[idx]._tempUrl; // Clear temp URL after success
                renderEditorBlocks(); 
                updatePreview(); 
            }
        }

        async function saveStory() {
            const session = JSON.parse(localStorage.getItem('session'));
            const isNew = !currentEditingStory.uri;
            const rkey = isNew ? undefined : currentEditingStory.uri.split('/').pop();
            currentEditingStory.value.title = document.getElementById('editor-title').value;
            currentEditingStory.value.isDraft = document.getElementById('editor-draft').checked;
            
            // Sanitize blocks before saving
            const cleanBlocks = currentEditingStory.value.blocks.map(b => {
                const { _tempUrl, ...rest } = b;
                return rest;
            });

            await fetch('https://bsky.social/xrpc/com.atproto.repo.' + (isNew ? 'createRecord' : 'putRecord'), {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.accessJwt },
                body: JSON.stringify({ 
                    repo: session.did, 
                    collection: 'id.gamedev.notes', 
                    rkey, 
                    record: { 
                        $type: 'id.gamedev.notes', 
                        ...currentEditingStory.value, 
                        blocks: cleanBlocks, 
                        updatedAt: new Date().toISOString() 
                    } 
                })
            });
            alert('Saved!'); location.reload();
        }

        async function deleteStory() {
            if (!confirm('Are you sure you want to delete this story?')) return;
            const session = JSON.parse(localStorage.getItem('session'));
            const rkey = currentEditingStory.uri.split('/').pop();
            await fetch('https://bsky.social/xrpc/com.atproto.repo.deleteRecord', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.accessJwt },
                body: JSON.stringify({ repo: session.did, collection: 'id.gamedev.notes', rkey })
            });
            alert('Deleted!'); location.reload();
        }

        function viewStory(idx) {
            const note = DATA.notes[idx];
            const body = document.getElementById('viewer-body');
            let html = \`<h1 class="heading-font" style="font-size: 3rem; margin-bottom: 2rem; font-weight:900;">\${parseMarkdown(note.value.title)} \${note.value.isDraft ? '<span class="draft-badge">DRAFT</span>' : ''}</h1>\`;
            note.value.blocks.forEach(b => {
                const url = resolveImageUrl(DATA.profile.did, b.blob, b.content, b);
                if (b.type === 'text') html += \`<p style="margin-bottom:1.5rem; line-height:1.8; opacity:0.8;">\${parseMarkdown(b.content)}</p>\`;
                else if (b.type === 'h1') html += \`<h2 class="heading-font" style="color:var(--primary); margin:2rem 0 1rem; font-size:2rem;">\${parseMarkdown(b.content)}</h2>\`;
                else if (b.type === 'image') html += \`<img src="\${url}" style="width:100%; border-radius:1.5rem; margin:1.5rem 0; border: 1px solid var(--border);">\`;
                else if (b.type === 'quote') html += \`<blockquote>\${parseMarkdown(b.content)}</blockquote>\`;
                else if (b.type === 'code') html += \`<pre>\${b.content}</pre>\`;
                else if (b.type === 'dash') html += '<hr class="dash-line">';
            });
            body.innerHTML = html;
            
            const editBtn = document.getElementById('viewer-edit-btn');
            if (isEditMode) {
                editBtn.style.display = 'block';
                editBtn.onclick = () => { closeStory(); openStoryEditor(idx); };
            } else { editBtn.style.display = 'none'; }

            document.getElementById('story-viewer').classList.add('active');
            document.body.style.overflow = 'hidden';
            window.scrollTo({ top: 0 });
        }

        function closeStory() { document.getElementById('story-viewer').classList.remove('active'); document.body.style.overflow = ''; }
        function switchTab(tab) {
            document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
            document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
            document.querySelector(\`button[onclick="switchTab('\${tab}')"]\`).classList.add('active');
            document.getElementById('tab-' + tab).classList.add('active');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        function toggleEditMode(force = null) {
            if (localStorage.getItem('session')) {
                isEditMode = force !== null ? force : !isEditMode;
                document.getElementById('menu-edit-label').textContent = isEditMode ? 'EXIT EDIT MODE' : 'EDIT PORTFOLIO';
                document.getElementById('menu-logout').style.display = isEditMode ? 'block' : 'none';
                render();
            } else { document.getElementById('login-modal').style.display = 'flex'; }
        }

        async function handleLogin() {
            const ide = document.getElementById('login-handle').value;
            const pas = document.getElementById('login-pass').value;
            const res = await fetch('https://bsky.social/xrpc/com.atproto.server.createSession', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ identifier:ide, password:pas }) });
            if (!res.ok) return alert('Failed');
            localStorage.setItem('session', JSON.stringify(await res.json()));
            location.reload();
        }

        function logout() { localStorage.removeItem('session'); location.reload(); }
        init();
    </script>
</body>
</html>`;
}

export function downloadPortfolio(html: string, filename: string) {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
